import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/use-auth';
import { useYearContext } from '../contexts/YearContext';
import { Navbar } from '../components/navbar';
import { Sidebar } from '../components/sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/select';
import { YearControls } from '../components/year-controls';
import { DualRingDonutChart } from '../components/charts/dual-ring-donut-chart';
import { EmissionProgressChart } from '../components/charts/emission-progress-chart';
import { BaselineComparisonChart } from '../components/charts/baseline-comparison-chart';
import { DualAxisComboChart } from '../components/charts/dual-axis-combo-chart';
import { VarianceAnalysisChart } from '../components/charts/variance-analysis-chart';
import { BaselineKPICards } from '../components/charts/baseline-kpi-cards';
import { ClusteredBarChart } from '../components/charts/clustered-bar-chart';
import { ScopeYearComparisonChart } from '../components/charts/scope-year-comparison-chart';
import { ComparisonModeCard } from '../components/charts/comparison-mode-card';
import { ScopeComparisonCards } from '../components/dashboard/scope-comparison-cards';
import { KPIHeaderCards } from '../components/dashboard/kpi-header-cards';
import { FilterBar } from '../components/dashboard/filter-bar';
import { EmissionCategoryCards } from '../components/dashboard/emission-category-cards';
import { EmissionsOverTimeAreaChart } from '../components/charts/emissions-over-time-area-chart';
import { EmissionsByCategoryDonut } from '../components/charts/emissions-by-category-donut';
import { getQueryFn } from '../lib/queryClient';
import { useLocation } from 'wouter';
import { 
  groupEmissionsByCategory, 
  calculateCategoryData, 
  EmissionCategory,
  CategoryData 
} from '../lib/emission-categories';

// Interface for year-wise emissions data
interface YearWiseEmissionsData {
  year: number;
  scope1: number;
  scope2: number;
  scope3: number;
  total: number;
}

// Interface for the new base year emissions endpoint response
interface BaseYearEmissionsResponse {
  base_year: number;
  base_year_data: YearWiseEmissionsData | null;
  reporting_years_data: YearWiseEmissionsData[];
}

export default function Dashboard() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { 
    baseYear, 
    selectedYear, 
    comparisonYear, 
    isComparisonMode, 
    currentCompany,
    availableYears,
    companies,
    setSelectedYear,
    setComparisonYear,
    setCurrentCompany,
    toggleComparisonMode
  } = useYearContext();

  // Initialize feather icons
  useEffect(() => {
    if (window.feather) {
      window.feather.replace();
    }
  }, []);

  // Company selection is now handled by YearContext

  const { data: emissionSummary, isLoading: emissionSummaryLoading, error: emissionSummaryError } = useQuery({
    queryKey: currentCompany?.id && selectedYear ? 
      ['/api/emissions/summary/' + currentCompany.id, { year: selectedYear, base_year: baseYear }] : 
      [null],
    queryFn: getQueryFn(),
    enabled: !!currentCompany?.id && !!selectedYear,
  });

  // Fetch activity type emissions data for current year
  const { data: activityTypeData, error: activityTypeError } = useQuery({
    queryKey: currentCompany?.id && selectedYear ?
      ['/api/emissions/by-activity-type', { company_id: currentCompany.id, year: selectedYear }] :
      [null],
    queryFn: getQueryFn(),
    enabled: !!currentCompany?.id && !!selectedYear,
  });

  // Fetch activity type emissions data for baseline year
  const { data: baselineActivityTypeData } = useQuery({
    queryKey: currentCompany?.id && baseYear && isComparisonMode ?
      ['/api/emissions/by-activity-type', { company_id: currentCompany.id, year: baseYear }] :
      [null],
    queryFn: getQueryFn(),
    enabled: !!currentCompany?.id && !!baseYear && isComparisonMode,
  });

  // Fetch emissions data by country
  const { data: countryEmissionsData } = useQuery({
    queryKey: currentCompany?.id && selectedYear ?
      ['/api/emissions/by-country', { company_id: currentCompany.id, year: selectedYear }] :
      [null],
    queryFn: getQueryFn(),
    enabled: !!currentCompany?.id && !!selectedYear,
  });

  // Fetch monthly emissions data for area chart
  const { data: monthlyEmissionsData } = useQuery({
    queryKey: currentCompany?.id && selectedYear ?
      ['/api/emissions/by-month', { company_id: currentCompany.id, year: selectedYear }] :
      [null],
    queryFn: getQueryFn(),
    enabled: !!currentCompany?.id && !!selectedYear,
  });

  // Fetch year-wise emissions data for comparison
  const { data: yearWiseEmissionsData } = useQuery({
    queryKey: currentCompany?.id ?
      ['/api/emissions/by-year', { company_id: currentCompany.id }] :
      [null],
    queryFn: getQueryFn(),
    enabled: !!currentCompany?.id,
  });

  // Debug: Also try the debug endpoint
  const { data: debugYearWiseEmissionsData } = useQuery({
    queryKey: currentCompany?.id ?
      ['/api/emissions/debug-by-year', { company_id: currentCompany.id }] :
      [null],
    queryFn: getQueryFn(),
    enabled: !!currentCompany?.id,
  });

  // NEW: Use the optimized base year emissions endpoint
  const { data: baseYearEmissionsData, isLoading: baseYearEmissionsLoading, error: baseYearEmissionsError } = useQuery({
    queryKey: currentCompany?.id ?
      [`/api/emissions/base-year/${currentCompany.id}`] :
      [null],
    queryFn: getQueryFn(),
    enabled: !!currentCompany?.id,
  });

  // Use real data from the API
  const displayEmissionSummary = emissionSummary;

  // Extract base year and reporting year data for comparison
  const baseYearData = React.useMemo(() => {
    // First try to use the new optimized endpoint
    if (baseYearEmissionsData?.base_year_data) {
      return baseYearEmissionsData.base_year_data;
    }
    
    // Fallback to year-wise data
    if (!yearWiseEmissionsData || !baseYear) {
      return null;
    }
    
    const found = yearWiseEmissionsData.find((d: YearWiseEmissionsData) => d.year === baseYear);
    return found || null;
  }, [baseYearEmissionsData, yearWiseEmissionsData, baseYear]);

  const reportingYearData = React.useMemo(() => {
    // First try to use the new optimized endpoint
    if (baseYearEmissionsData && selectedYear) {
      // If selected year is the base year, use base_year_data
      if (baseYearEmissionsData.base_year === selectedYear && baseYearEmissionsData.base_year_data) {
        return baseYearEmissionsData.base_year_data;
      }
      
      // Otherwise, look in reporting_years_data
      if (baseYearEmissionsData.reporting_years_data) {
        return baseYearEmissionsData.reporting_years_data.find((d: YearWiseEmissionsData) => d.year === selectedYear) || null;
      }
    }
    
    // Fallback to year-wise data
    if (!yearWiseEmissionsData || !selectedYear) return null;
    return yearWiseEmissionsData.find((d: YearWiseEmissionsData) => d.year === selectedYear) || null;
  }, [baseYearEmissionsData, yearWiseEmissionsData, selectedYear]);


  // Implement real baseline comparison data from API
  const baselineComparisonData = React.useMemo(() => {
    // First try to use the new optimized base year endpoint
    if (baseYearEmissionsData?.base_year_data) {
      let selectedYearData = null;
      
      // If selected year is the base year, use base_year_data
      if (baseYearEmissionsData.base_year === selectedYear) {
        selectedYearData = baseYearEmissionsData.base_year_data;
      } else if (baseYearEmissionsData.reporting_years_data) {
        // Otherwise, look in reporting_years_data
        selectedYearData = baseYearEmissionsData.reporting_years_data.find(
          (d: YearWiseEmissionsData) => d.year === selectedYear
        );
      }
      
      if (selectedYearData) {
        return {
          baseYear: {
            scope1: baseYearEmissionsData.base_year_data.scope1 || 0,
            scope2: baseYearEmissionsData.base_year_data.scope2 || 0,
            scope3: baseYearEmissionsData.base_year_data.scope3 || 0,
            total: baseYearEmissionsData.base_year_data.total || 0,
          },
          selectedYear: {
            scope1: selectedYearData.scope1 || 0,
            scope2: selectedYearData.scope2 || 0,
            scope3: selectedYearData.scope3 || 0,
            total: selectedYearData.total || 0,
          }
        };
      }
    }
    
    // Fallback to enhanced summary data with base year comparison
    if (emissionSummary?.base_year_data) {
      return {
        baseYear: {
          scope1: emissionSummary.base_year_data.scope_totals?.scope1 || 0,
          scope2: emissionSummary.base_year_data.scope_totals?.scope2 || 0,
          scope3: emissionSummary.base_year_data.scope_totals?.scope3 || 0,
          total: emissionSummary.base_year_data.total_emissions || 0,
        },
        selectedYear: {
          scope1: emissionSummary.scope_totals?.scope1 || 0,
          scope2: emissionSummary.scope_totals?.scope2 || 0,
          scope3: emissionSummary.scope_totals?.scope3 || 0,
          total: emissionSummary.total_emissions || 0,
        }
      };
    }
    
    // Final fallback to year-wise data if available
    if (baseYearData && reportingYearData) {
      return {
        baseYear: {
          scope1: baseYearData.scope1 || 0,
          scope2: baseYearData.scope2 || 0,
          scope3: baseYearData.scope3 || 0,
          total: baseYearData.total || 0,
        },
        selectedYear: {
          scope1: reportingYearData.scope1 || 0,
          scope2: reportingYearData.scope2 || 0,
          scope3: reportingYearData.scope3 || 0,
          total: reportingYearData.total || 0,
        }
      };
    }
    
    return null;
  }, [baseYearEmissionsData, emissionSummary, baseYearData, reportingYearData, selectedYear]);

  // TODO: Implement real trend data from API
  const trendData: any = null;

  // Process category data for new components
  const categoryData = useMemo(() => {
    if (!activityTypeData) return {} as Record<EmissionCategory, CategoryData>;
    
    const grouped = groupEmissionsByCategory(activityTypeData);
    return calculateCategoryData(grouped);
  }, [activityTypeData]);

  const baseYearCategoryData = useMemo(() => {
    if (!baselineActivityTypeData) return undefined;
    
    const grouped = groupEmissionsByCategory(baselineActivityTypeData);
    return calculateCategoryData(grouped);
  }, [baselineActivityTypeData]);

  // Process data for category donut chart
  const categoryDonutData = useMemo(() => {
    return Object.entries(categoryData).map(([category, data]) => ({
      category: category as EmissionCategory,
      value: data.totalEmissions / 1000, // Convert to MT
      percentage: 0 // Will be calculated in component
    }));
  }, [categoryData]);

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar />
      
      <div className="flex">
        <Sidebar />
        
        <main className="flex-1 p-8 pb-16">
          <div className="max-w-7xl mx-auto">
            {/* 1. Header Section with KPI Cards */}
            {displayEmissionSummary && (
              <KPIHeaderCards
                totalEmissions={displayEmissionSummary.total_emissions || 0}
                offsets={0} // Placeholder for future offsets feature
                companyName={currentCompany?.name}
                selectedYear={selectedYear || undefined}
              />
            )}

            {/* 2. Enhanced Filter Bar */}
            <FilterBar
              selectedYear={selectedYear || undefined}
              availableYears={availableYears}
              onYearChange={setSelectedYear}
            />

            {/* 3. Existing Selectors */}
            <div className="mb-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Company Selector */}
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-2">
                    Select Company
                  </label>
                  <Select
                    value={currentCompany?.id?.toString() || ''}
                    onChange={(e) => {
                      const companyId = parseInt(e.target.value);
                      if (companies) {
                        const company = companies.find(c => c.id === companyId);
                        if (company) {
                          setCurrentCompany(company);
                        }
                      }
                    }}
                    options={[
                      { value: '', label: 'Select Company' },
                      ...(companies || []).map(company => ({
                        value: company.id.toString(),
                        label: company.name
                      }))
                    ]}
                    className="w-full"
                  />
                </div>

                {/* Base Year Display */}
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-2">
                    Base Year
                  </label>
                  <div className="px-3 py-2 border border-green-300 rounded-md bg-green-50 text-green-700">
                    {baseYear || 'Not Set'}
                  </div>
                  <p className="text-xs text-green-500 mt-1">
                    Set in company profile
                  </p>
                </div>

                {/* Reporting Year */}
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-2">
                    Reporting Year
                  </label>
                  <Select
                    value={selectedYear?.toString() || ''}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    options={[
                      { value: '', label: 'Select Year' },
                      ...(availableYears || []).map(year => ({
                        value: year.toString(),
                        label: year.toString()
                      }))
                    ]}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* 4. NEW: Emission Category Cards Section */}
            {Object.keys(categoryData).length > 0 && (
              <EmissionCategoryCards
                categoryData={categoryData}
                baseYearData={baseYearCategoryData}
                selectedYear={selectedYear || undefined}
                baseYear={baseYear || undefined}
              />
            )}

            {/* 5. NEW: Top Priority Charts (3-column grid) */}
            {displayEmissionSummary && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                {/* Emissions over Time (Area Chart) */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center text-green-800">
                      <i data-feather="trending-up" className="w-5 h-5 mr-2"></i>
                      Emissions over Time
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {monthlyEmissionsData ? (
                      <EmissionsOverTimeAreaChart 
                        data={monthlyEmissionsData} 
                        selectedYear={selectedYear || undefined}
                      />
                    ) : (
                      <div className="h-80 flex items-center justify-center text-green-600">
                        <div className="text-center">
                          <i data-feather="bar-chart" className="w-12 h-12 mx-auto mb-4"></i>
                          <p>Loading monthly data...</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Emissions by Category (Donut) */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-green-800">
                      <i data-feather="pie-chart" className="w-5 h-5 mr-2"></i>
                      Emissions by Category
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {categoryDonutData.length > 0 ? (
                      <EmissionsByCategoryDonut 
                        data={categoryDonutData} 
                        selectedYear={selectedYear || undefined}
                      />
                    ) : (
                      <div className="h-80 flex items-center justify-center text-green-600">
                        <div className="text-center">
                          <i data-feather="pie-chart" className="w-12 h-12 mx-auto mb-4"></i>
                          <p>No category data available</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Emissions by Scope (Donut) - Keep existing */}
                <Card className="lg:col-span-3">
                  <CardHeader>
                    <CardTitle className="flex items-center text-green-800">
                      <i data-feather="pie-chart" className="w-5 h-5 mr-2"></i>
                      Emissions by Scope
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DualRingDonutChart 
                      baseYearData={baseYearData ? {
                        scope1: baseYearData.scope1,
                        scope2: baseYearData.scope2,
                        scope3: baseYearData.scope3,
                        total: baseYearData.total
                      } : null}
                      reportingYearData={displayEmissionSummary ? {
                        scope1: displayEmissionSummary.scope_totals?.scope1 || 0,
                        scope2: displayEmissionSummary.scope_totals?.scope2 || 0,
                        scope3: displayEmissionSummary.scope_totals?.scope3 || 0,
                        total: displayEmissionSummary.total_emissions || 0
                      } : null}
                      baseYear={baseYear || 0}
                      reportingYear={selectedYear || 0}
                    />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* 6. Existing Summary Cards (4 cards) */}
            {displayEmissionSummary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Total Emissions</p>
                      <p className="text-2xl font-bold text-green-900">
                        {displayEmissionSummary?.total_emissions?.toFixed(2) || '0'}
                      </p>
                      <p className="text-xs text-green-500">kg CO2e</p>
                    </div>
                    <i data-feather="trending-up" className="w-8 h-8 text-green-400"></i>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Entries</p>
                      <p className="text-2xl font-bold text-green-900">
                        {displayEmissionSummary?.entry_count || 0}
                      </p>
                      <p className="text-xs text-green-500">Total records</p>
                    </div>
                    <i data-feather="file-text" className="w-8 h-8 text-green-400"></i>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Measured Data</p>
                      <p className="text-2xl font-bold text-green-600">
                        {displayEmissionSummary?.breakdown?.measured || 0}
                      </p>
                      <p className="text-xs text-green-500">Direct measurements</p>
                    </div>
                    <i data-feather="check-circle" className="w-8 h-8 text-green-400"></i>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Estimated Data</p>
                      <p className="text-2xl font-bold text-green-600">
                        {displayEmissionSummary?.breakdown?.estimated || 0}
                      </p>
                      <p className="text-xs text-green-500">Estimations</p>
                    </div>
                    <i data-feather="alert-circle" className="w-8 h-8 text-green-400"></i>
                  </div>
                </CardContent>
              </Card>
            </div>
            )}


            {/* Scope Comparison Cards */}
            {displayEmissionSummary && (
              <div className="mb-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center text-xs">
                      <i data-feather="layers" className="w-3 h-3 mr-1"></i>
                      Scope Comparison: Base Year vs Reporting Year
                    </CardTitle>
                    <p className="text-xs text-gray-600 mt-1">
                      Detailed breakdown of Scope 1, 2, and 3 emissions comparing base year ({baseYear}) with reporting year ({selectedYear}).
                    </p>
                  </CardHeader>
                  <CardContent>
                    <ScopeComparisonCards 
                data={{
                        baseYear: baseYearData || {
                          scope1: 0,
                          scope2: 0,
                          scope3: 0,
                          total: 0,
                          year: baseYear || 0
                        },
                        selectedYear: reportingYearData || {
                          scope1: displayEmissionSummary.scope_totals?.scope1 || 0,
                          scope2: displayEmissionSummary.scope_totals?.scope2 || 0,
                          scope3: displayEmissionSummary.scope_totals?.scope3 || 0,
                          total: displayEmissionSummary.total_emissions || 0,
                          year: selectedYear || 0
                        },
                  baseYearValue: baseYear || 0,
                  selectedYearValue: selectedYear || 0,
                }}
              />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Summary Cards */}
            {displayEmissionSummary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <Card>
                <CardContent className="pt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Total Emissions</p>
                      <p className="text-sm font-bold text-gray-900">
                        {displayEmissionSummary?.total_emissions?.toFixed(2) || '0'}
                      </p>
                      <p className="text-xs text-gray-500">kg CO2e</p>
                    </div>
                    <i data-feather="trending-up" className="w-4 h-4 text-gray-400"></i>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Entries</p>
                      <p className="text-sm font-bold text-gray-900">
                        {displayEmissionSummary?.entry_count || 0}
                      </p>
                      <p className="text-xs text-gray-500">Total records</p>
                    </div>
                    <i data-feather="file-text" className="w-4 h-4 text-gray-400"></i>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Measured Data</p>
                      <p className="text-sm font-bold text-green-600">
                        {displayEmissionSummary?.breakdown?.measured || 0}
                      </p>
                      <p className="text-xs text-gray-500">Direct measurements</p>
                    </div>
                    <i data-feather="check-circle" className="w-4 h-4 text-green-400"></i>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Estimated Data</p>
                      <p className="text-sm font-bold text-orange-600">
                        {displayEmissionSummary?.breakdown?.estimated || 0}
                      </p>
                      <p className="text-xs text-gray-500">Estimations</p>
                    </div>
                    <i data-feather="alert-circle" className="w-4 h-4 text-orange-400"></i>
                  </div>
                </CardContent>
              </Card>
            </div>
            )}

            {/* Comparison Mode Indicator */}
            {isComparisonMode && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <i data-feather="trending-up" className="w-5 h-5 text-blue-600"></i>
                  <span className="text-blue-800 font-medium">
                    Comparison Mode: {selectedYear} vs {baseYear} (Base Year)
                  </span>
                </div>
                <p className="text-blue-700 text-sm mt-1">
                  Dashboard showing side-by-side comparison between base year and reporting year
                </p>
              </div>
            )}

            {/* Baseline KPI Cards */}
            {isComparisonMode && baselineComparisonData && (
              <BaselineKPICards 
                data={{
                  baseYear: { total: baselineComparisonData.baseYear?.total || 0 },
                  selectedYear: { total: baselineComparisonData.selectedYear?.total || 0 },
                  baseYearValue: baseYear || 0,
                  selectedYearValue: selectedYear || 0,
                }}
              />
            )}


             {/* Baseline Comparison Charts */}
             {isComparisonMode && baselineComparisonData && (
               <div className="mb-6">
                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Baseline Comparison Chart */}
                  <Card className="overflow-hidden">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-semibold leading-snug text-gray-900 flex items-center">
                        <i data-feather="bar-chart-2" className="w-4 h-4 mr-2 shrink-0"></i>
                        Baseline vs Selected Year
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="min-w-0">
                      <BaselineComparisonChart 
                        data={{
                          baseYear: baselineComparisonData.baseYear,
                          selectedYear: baselineComparisonData.selectedYear,
                          baseYearValue: baseYear || 0,
                          selectedYearValue: selectedYear || 0,
                        }}
                      />
                    </CardContent>
                  </Card>

                  {/* Variance Analysis Chart */}
                  <Card className="overflow-hidden">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-semibold leading-snug text-gray-900 flex items-center">
                        <i data-feather="trending-up" className="w-4 h-4 mr-2 shrink-0"></i>
                        Variance Analysis
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="min-w-0">
                      <VarianceAnalysisChart 
                        data={{
                          baseYear: baselineComparisonData.baseYear,
                          selectedYear: baselineComparisonData.selectedYear,
                          baseYearValue: baseYear || 0,
                          selectedYearValue: selectedYear || 0,
                        }}
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Standard Charts Section - Always show when data is available */}
            {displayEmissionSummary && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
                {/* Dual-Ring Donut Chart */}
                <Card className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold leading-snug text-gray-900 flex items-start">
                      <i data-feather="pie-chart" className="w-4 h-4 mr-2 mt-0.5 shrink-0"></i>
                      Comparison of Emission by Scope — Base Year vs Reporting Year
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="min-w-0">
                    <DualRingDonutChart 
                      baseYearData={baseYearData ? {
                        scope1: baseYearData.scope1,
                        scope2: baseYearData.scope2,
                        scope3: baseYearData.scope3,
                        total: baseYearData.total
                      } : null}
                      reportingYearData={displayEmissionSummary ? {
                        scope1: displayEmissionSummary.scope_totals?.scope1 || 0,
                        scope2: displayEmissionSummary.scope_totals?.scope2 || 0,
                        scope3: displayEmissionSummary.scope_totals?.scope3 || 0,
                        total: displayEmissionSummary.total_emissions || 0
                      } : null}
                      baseYear={baseYear || 0}
                      reportingYear={selectedYear || 0}
                    />
                  </CardContent>
                </Card>

                {/* Dual Axis Combo Chart */}
                <Card className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold leading-snug text-gray-900 flex items-center">
                      <i data-feather="bar-chart-2" className="w-4 h-4 mr-2 shrink-0"></i>
                      Dual Axis Combo Chart
                    </CardTitle>
                    <p className="text-xs text-gray-600 mt-1">
                      Total emissions trend (line) with scope breakdown (bars)
                    </p>
                  </CardHeader>
                  <CardContent className="min-w-0">
                    <DualAxisComboChart 
                      data={{
                        years: [
                          {
                            year: baseYear || 0,
                            scope1: baseYearData?.scope1 || 0,
                            scope2: baseYearData?.scope2 || 0,
                            scope3: baseYearData?.scope3 || 0,
                            total: baseYearData?.total || 0
                          },
                          {
                            year: selectedYear || 0,
                            scope1: displayEmissionSummary.scope_totals?.scope1 || 0,
                            scope2: displayEmissionSummary.scope_totals?.scope2 || 0,
                            scope3: displayEmissionSummary.scope_totals?.scope3 || 0,
                            total: displayEmissionSummary.total_emissions || 0
                          }
                        ].sort((a, b) => (a.year || 0) - (b.year || 0)),
                        baseYear: baseYear || 0,
                      }}
                    />
                  </CardContent>
                </Card>

                {/* Emission Progress Chart */}
                <Card className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold leading-snug text-gray-900 flex items-center">
                      <i data-feather="trending-down" className="w-4 h-4 mr-2 shrink-0"></i>
                      Emission Reduction Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="min-w-0">
                    <EmissionProgressChart data={displayEmissionSummary.progress} />
                  </CardContent>
                </Card>

                 {/* Variance Analysis Chart */}
                 <Card className="overflow-hidden">
                   <CardHeader className="pb-3">
                     <CardTitle className="text-sm font-semibold leading-snug text-gray-900 flex items-center">
                       <i data-feather="trending-up" className="w-4 h-4 mr-2 shrink-0"></i>
                       Variance Analysis
                     </CardTitle>
                     <p className="text-xs text-gray-600 mt-1">
                       {isComparisonMode 
                         ? `Compare emissions variance between base year (${baseYear}) and reporting year (${selectedYear}).`
                         : `View current year emissions variance. Enable comparison mode to compare with base year (${baseYear}).`
                       }
                     </p>
                   </CardHeader>
                   <CardContent className="min-w-0">
                     {displayEmissionSummary ? (
                       <VarianceAnalysisChart 
                         data={{
                           baseYear: baseYearData || {
                             scope1: 0,
                             scope2: 0,
                             scope3: 0,
                             total: 0,
                             year: baseYear || 0
                           },
                           selectedYear: reportingYearData || {
                             scope1: displayEmissionSummary.scope_totals?.scope1 || 0,
                             scope2: displayEmissionSummary.scope_totals?.scope2 || 0,
                             scope3: displayEmissionSummary.scope_totals?.scope3 || 0,
                             total: displayEmissionSummary.total_emissions || 0,
                             year: selectedYear || 0
                           },
                           baseYearValue: baseYear || 0,
                           selectedYearValue: selectedYear || 0,
                         }}
                       />
                     ) : (
                       <div className="text-center py-8 text-gray-500">
                         <div className="w-12 h-12 mx-auto mb-4 text-gray-400">
                           <i data-feather="trending-up" className="w-12 h-12"></i>
                         </div>
                         <p>No emission data available for variance analysis</p>
                         <p className="text-sm mt-2">
                           Select a company and year to view variance analysis
                         </p>
                       </div>
                     )}
                   </CardContent>
                 </Card>
              </div>
            )}


            {/* Additional Professional Charts */}

            {/* Comparison Mode Card - Show when comparison mode is enabled */}
            {isComparisonMode && baseYearData && reportingYearData && (
              <div className="mb-8">
                <ComparisonModeCard 
                  baseYearData={baseYearData}
                  reportingYearData={reportingYearData}
                  baseYear={baseYear!}
                  reportingYear={selectedYear!}
                />
              </div>
            )}

            {/* Extra spacing to prevent overlap */}
            <div className="mt-16"></div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
              <Card>
                <CardContent className="pt-3 text-center">
                  <i data-feather="upload" className="w-4 h-4 mx-auto mb-2 text-blue-600"></i>
                  <h3 className="text-xs font-medium mb-1">Quick Upload</h3>
                  <p className="text-xs text-gray-600 mb-2">
                    Upload emission data from CSV or Excel files
                  </p>
                  <Button size="sm" onClick={() => setLocation('/scope/scope1')}>
                    Upload Data
                  </Button>
                </CardContent>
              </Card>

               <Card>
                 <CardContent className="pt-3 text-center">
                   <i data-feather="settings" className="w-4 h-4 mx-auto mb-2 text-green-600"></i>
                   <h3 className="text-xs font-medium mb-1">Estimation Wizard</h3>
                   <p className="text-xs text-gray-600 mb-2">
                     Estimate emissions when direct data isn't available
                   </p>
                   <Button size="sm" variant="outline" onClick={() => setLocation('/scope/scope1')}>
                     Start Wizard
                   </Button>
                 </CardContent>
               </Card>

               <Card>
                 <CardContent className="pt-3 text-center">
                   <i data-feather="home" className="w-4 h-4 mx-auto mb-2 text-purple-600"></i>
                   <h3 className="text-xs font-medium mb-1">Company Profile</h3>
                   <p className="text-xs text-gray-600 mb-2">
                     Manage company information and settings
                   </p>
                   <Button size="sm" variant="outline" onClick={() => setLocation('/company')}>
                     View Profile
                   </Button>
                 </CardContent>
               </Card>
            </div>

            {/* No Data Messages */}
            {!currentCompany && (
              <Card className="mt-8">
                <CardContent className="pt-6 text-center">
                  <i data-feather="alert-triangle" className="w-12 h-12 mx-auto mb-4 text-yellow-600"></i>
                  <h3 className="text-lg font-medium mb-2">No Company Profile</h3>
                  <p className="text-gray-600 mb-4">
                    Please create a company profile to start tracking emissions.
                  </p>
                  {/* Only show Create Company button for system users */}
                  {user?.is_system_user && (
                    <Button onClick={() => setLocation('/company')}>
                      Create Company Profile
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {currentCompany && !selectedYear && (
              <Card className="mt-8">
                <CardContent className="pt-6 text-center">
                  <i data-feather="calendar" className="w-12 h-12 mx-auto mb-4 text-blue-600"></i>
                  <h3 className="text-lg font-medium mb-2">Select a Year</h3>
                  <p className="text-gray-600 mb-4">
                    Please select a year from the Year Context controls above to view emission data.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Loading State */}
            {emissionSummaryLoading && (
              <Card className="mt-8">
                <CardContent className="pt-6 text-center">
                  <i data-feather="loader" className="w-12 h-12 mx-auto mb-4 text-blue-400 animate-spin"></i>
                  <h3 className="text-lg font-medium mb-2">Loading Environmental Data</h3>
                  <p className="text-gray-600">
                    Fetching data for {currentCompany?.name} in {selectedYear}...
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Error State */}
            {emissionSummaryError && (
              <Card className="mt-8">
                <CardContent className="pt-6 text-center">
                  <i data-feather="alert-triangle" className="w-12 h-12 mx-auto mb-4 text-red-400"></i>
                  <h3 className="text-lg font-medium mb-2">Error Loading Data</h3>
                  <p className="text-gray-600 mb-4">
                    Failed to load environmental data. Showing sample data instead.
                  </p>
                  <p className="text-sm text-gray-500">
                    Error: {emissionSummaryError.message || 'Unknown error'}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* No Data State */}
            {currentCompany && selectedYear && !displayEmissionSummary && !emissionSummaryLoading && !emissionSummaryError && (
              <Card className="mt-8">
                <CardContent className="pt-6 text-center">
                  <i data-feather="bar-chart" className="w-12 h-12 mx-auto mb-4 text-gray-400"></i>
                  <h3 className="text-lg font-medium mb-2">No Emission Data</h3>
                  <p className="text-gray-600 mb-4">
                    No environmental data found for {selectedYear}. Start by adding some emission entries.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <Button onClick={() => setLocation('/scope/scope1')}>
                      Add Scope 1 Data
                    </Button>
                    <Button variant="outline" onClick={() => setLocation('/scope/scope2')}>
                      Add Scope 2 Data
                    </Button>
                    <Button 
                      onClick={() => window.open('/check_and_insert_sample_data.sql', '_blank')}
                      variant="outline"
                    >
                      <i data-feather="database" className="w-4 h-4 mr-2"></i>
                      Load Sample Data
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>
      {/* Bottom padding to prevent overlapping */}
      <div className="h-8"></div>
    </div>
  );
}
